/*
 * *****************************************************************************
 *     Copyright (C)  Motorola Solutions, INC.
 *     All Rights Reserved.
 *     Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectCallAlerts } from '../../configuration/+state/configuration.selectors';
import { CallAlertTracker } from '../model/call-alert-tracker';
import { selectIsAlerting } from '../+state/call.selectors';
import { CallAlertFunctions } from '../util/call-alert-functions';
import { map, switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { CallAlert } from '../model/audible-alert';
import { AlertService } from '../../core/audio/alert.service';
import {
    callAlertDelete,
    callAlertUpdate
} from '../../configuration/+state/configuration.actions';
import { StompManagerService } from '../../core/services/stomp-manager/stomp-manager.service';
import { StompBaseConsumer } from '../../core/abstract/stomp-base-consumer';
import { Topic } from '../../user/model/multiple-subscriptions';

@Injectable({
    providedIn: 'root'
})
export class CallAlertService extends StompBaseConsumer {
    private callAlertTrackers: CallAlertTracker[] = [];

    constructor(
        private http: HttpClient,
        private alertService: AlertService,
        protected store: Store,
        protected stompManager: StompManagerService
    ) {
        super(stompManager, store);
        this.store.select(selectCallAlerts)
            .pipe(switchMap((callAlerts) => callAlerts?.length ?
                forkJoin(callAlerts.filter((callAlert) =>
                    callAlert.filterConstraints.every((c) =>
                        CallAlertFunctions.MatchFunctions.hasOwnProperty(c.function)))
                    .map((callAlert) => this.getCallAlertTracker(callAlert))) : of([] as CallAlertTracker[])))
            .subscribe((callAlertTrackers) => this.initialize(callAlertTrackers));
    }


    protected handleMessageCallAlertUpdate(callAlert: CallAlert) {
        this.store.dispatch(callAlertUpdate({ callAlert }));
    }

    protected handleMessageCallAlertNew(callAlert: CallAlert) {
        this.store.dispatch(callAlertUpdate({ callAlert }));
    }

    protected handleMessageCallAlertDelete(callAlert: CallAlert) {
        this.store.dispatch(callAlertDelete({ callAlert }));
    }

    protected getTopics(): Topic[] {
        return [Topic.CALL_ALERT_UPDATE, Topic.CALL_ALERT_NEW, Topic.CALL_ALERT_DELETE];
    }

    protected getTopicHandlers(): { [p in Topic]?: (message: any) => void } {
        return {
            [Topic.CALL_ALERT_UPDATE]: this.handleMessageCallAlertUpdate.bind(this),
            [Topic.CALL_ALERT_NEW]: this.handleMessageCallAlertNew.bind(this),
            [Topic.CALL_ALERT_DELETE]: this.handleMessageCallAlertDelete.bind(this),
        };
    }

    handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Call alert topic ${topic} subscription success: ${receiptId}`);
    }

    handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Call alert topic ${topic} subscription end: ${receiptId}`);
    }

    handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} Call alert topic ${topic} subscription failed=${error} for receiptId=${receiptId}`);
    }

    public getCallAlertTracker(callAlert: CallAlert) {
        return this.getAudioBuffer(`${this.alertService.audioFileUrl}${callAlert.fileId}`).pipe(
            map((audioBuffer) => {
                return { ...callAlert, audioBuffer: audioBuffer } as CallAlertTracker;
            })
        );
    }

    private getAudioBuffer(url: string) {
        return this.http.get(url, { responseType: 'arraybuffer' }).pipe(switchMap((arrayBuffer) => this.alertService.audioContext.decodeAudioData(arrayBuffer)));
    }

    public deleteTracker(tracker: CallAlertTracker) {
        console.debug(`Deleting tracker: ${tracker.uuid}`);
        if (tracker.node) {
            this.requestStop(tracker);
        }
        tracker.isAlertingSubscription?.unsubscribe();
    }

    public initialize(trackers: CallAlertTracker[]) {
        console.debug('Initializing call alert trackers.');
        this.callAlertTrackers.forEach((tracker) => this.deleteTracker(tracker));
        this.callAlertTrackers = trackers;
        this.callAlertTrackers.forEach((tracker) => {
            console.debug(`Tracker: ${tracker.uuid} subscribing for alerting calls`);
            tracker.isAlertingSubscription = this.store.select(selectIsAlerting(tracker.uuid))
                .subscribe((isAlerting) => this.handleUpdate(tracker, isAlerting));
        });
    }

    public handleUpdate(tracker: CallAlertTracker, isAlerting: boolean) {
        if (isAlerting && !tracker.isPlaying) {
            this.requestPlay(tracker);
        }
        if (!isAlerting && tracker.repeat && tracker.isPlaying) {
            this.requestStop(tracker);
        }
    }

    public requestPlay(alertTracker: CallAlertTracker) {
        this.alertService.checkContext();
        console.debug(`Requesting play on alert: ${alertTracker.uuid} audio: ${alertTracker.fileName}`);
        alertTracker.isPlaying = true;
        alertTracker.node = this.alertService.audioContext.createBufferSource();
        alertTracker.node.buffer = alertTracker.audioBuffer;
        alertTracker.node.loop = alertTracker.repeat;
        alertTracker.node.onended = () => alertTracker.isPlaying = false;
        alertTracker.node.connect(this.alertService.mixerNode);
        alertTracker.node.start(this.alertService.audioContext.currentTime);
    }

    public requestStop(alertTracker: CallAlertTracker) {
        console.debug(`Requesting stop on alert: ${alertTracker.uuid} audio: ${alertTracker.fileName}`);
        alertTracker.node?.stop();
        alertTracker.node?.disconnect();
        alertTracker.node.onended = null;
        alertTracker.isPlaying = false;
    }

}
